import { useEffect, useRef, useState } from 'react'

/**
 * Reveal an answer as it is written, at a steady rate, instead of in
 * SSE-sized jumps.
 *
 * Gemini delivers deltas in bursts — a long pause during retrieval, then
 * several paragraphs at once — and grounded answers routinely arrive in two or
 * three frames. Rendering each burst verbatim reads as the whole answer landing
 * at once, which loses the sense that anything is being composed. Revealing on
 * a clock decouples what the reader sees from how the transport happened to
 * chunk it.
 *
 * The reveal owns its own ending: once it has started, it keeps writing until
 * it has caught up with the text, even after the stream closes. Otherwise the
 * final frame would dump whatever was still queued, which is exactly the
 * all-at-once effect this exists to remove.
 */

/** Reading pace when the reveal is keeping up with the stream. */
const BASE_CHARS_PER_SECOND = 220

/** Ceiling for catching up on a burst, so a long answer never crawls. */
const MAX_CHARS_PER_SECOND = 1200

/** Backlog target: what is queued now should be on screen within this window. */
const CATCH_UP_SECONDS = 1

/** Ignore long frame gaps (backgrounded tab) so we never dump the whole answer. */
const MAX_FRAME_SECONDS = 0.25

/**
 * No animation frame for this long while text is still queued: the page is
 * not being painted (hidden or occluded pane, background tab), so the reveal
 * stops waiting for frames and shows the rest.
 */
const NO_FRAME_FALLBACK_MS = 500

interface SmoothedReveal {
  /** The slice of the answer that is on screen right now. */
  text: string
  /** True while characters are still being written out. */
  revealing: boolean
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

export function useSmoothedReveal(
  text: string,
  streaming: boolean,
): SmoothedReveal {
  const [reduced] = useState(prefersReducedMotion)
  const [revealed, setRevealed] = useState(text.length)
  const revealedRef = useRef(text.length)
  const startedRef = useRef(streaming)
  const frameRef = useRef<number | null>(null)
  const lastFrameRef = useRef<number | null>(null)

  useEffect(() => {
    if (streaming) {
      startedRef.current = true
    }

    // A turn that was already complete when it mounted (thread history, an
    // error line) never animates: there was nothing to watch being written.
    if (reduced || !startedRef.current) {
      revealedRef.current = text.length
      lastFrameRef.current = null
      setRevealed(text.length)
      return
    }

    // A shorter body means this turn was replaced (retry, or a fresh answer
    // reusing the component), so start the reveal over rather than showing a
    // stale tail.
    if (revealedRef.current > text.length) {
      revealedRef.current = 0
      setRevealed(0)
    }

    // Caught up and waiting on the next delta: no frame loop until one lands.
    if (revealedRef.current >= text.length) {
      lastFrameRef.current = null
      return
    }

    // Frames are the clock, but a page that is not being painted gets none
    // while its fetches and timers keep running: the stream finishes, the
    // turn is complete, and the answer would sit half written until the page
    // is painted again. A timer notices the missing frames and shows the rest.
    let lastStepAt = performance.now()
    const watchdog = setInterval(() => {
      if (performance.now() - lastStepAt < NO_FRAME_FALLBACK_MS) {
        return
      }
      clearInterval(watchdog)
      if (frameRef.current != null) {
        cancelAnimationFrame(frameRef.current)
      }
      frameRef.current = null
      lastFrameRef.current = null
      revealedRef.current = text.length
      setRevealed(text.length)
    }, NO_FRAME_FALLBACK_MS / 2)

    const step = (now: number) => {
      lastStepAt = performance.now()
      const elapsed = Math.min(
        (now - (lastFrameRef.current ?? now)) / 1000,
        MAX_FRAME_SECONDS,
      )
      lastFrameRef.current = now

      const backlog = text.length - revealedRef.current
      const rate = Math.min(
        MAX_CHARS_PER_SECOND,
        Math.max(BASE_CHARS_PER_SECOND, backlog / CATCH_UP_SECONDS),
      )
      const next = revealedRef.current + rate * elapsed

      if (next >= text.length) {
        clearInterval(watchdog)
        revealedRef.current = text.length
        setRevealed(text.length)
        frameRef.current = null
        return
      }

      // Only re-render when a whole character appears; the markdown body is
      // expensive enough that a state update per frame is wasteful.
      const crossedCharacter = Math.floor(next) !== Math.floor(revealedRef.current)
      revealedRef.current = next
      if (crossedCharacter) {
        setRevealed(next)
      }
      frameRef.current = requestAnimationFrame(step)
    }

    frameRef.current = requestAnimationFrame(step)
    return () => {
      clearInterval(watchdog)
      if (frameRef.current != null) {
        cancelAnimationFrame(frameRef.current)
      }
      frameRef.current = null
      lastFrameRef.current = null
    }
  }, [text, streaming, reduced])

  const started = startedRef.current || streaming
  if (reduced || !started) {
    return { text, revealing: false }
  }

  const visible = Math.min(Math.floor(revealed), text.length)
  return { text: text.slice(0, visible), revealing: visible < text.length }
}
